import { Routes } from '@angular/router';
import {
  resolveTitleFor,
  Route,
  RouteFragment,
  RouteParam,
  RouteResolverData,
} from '@web/common';
import { PublicBoxAuthorizedDetailsGuard } from './guards/public-box-authorized-details.guard';
import { PublicBoxAuthorizationGuard } from './guards/public-box-authorization.guard';
import { accountQuestionnaireDetailsPendingChangesGuard } from './guards/account-questionnaire-details-pending-changes.guard';
import { EveryoneGuard, OnlyLoggedGuard } from '@web/shared/module/auth';
import { publicFetchFaqCategoriesResolver } from '@web/features/shop';
import { PublicProductAuthorizationGuard } from './guards/public-product-authorization.guard';
import { PublicProductAuthorizedDetailsGuard } from './guards/public-product-authorized-details.guard';

export const routes: Routes = [
  {
    path: RouteFragment.Empty,
    title: resolveTitleFor(Route.Root),
    canActivate: [EveryoneGuard],
    loadComponent: () =>
      import('./layouts/layout').then((c) => c.StandardLayoutComponent),
    children: [
      {
        path: RouteFragment.Empty,
        title: resolveTitleFor(Route.Root),
        loadComponent: () =>
          import('./pages/home').then((c) => c.HomePageComponent),
      },

      {
        path: RouteFragment.NotFound,
        title: resolveTitleFor(Route.NotFound),
        loadComponent: () =>
          import('./pages/error').then((c) => c.NotFoundPageComponent),
      },
      {
        path: RouteFragment.PL_Brands,
        children: [
          {
            path: RouteFragment.Empty,
            title: resolveTitleFor(Route.PL_Brands),
            loadComponent: () =>
              import('./pages/brand').then((c) => c.BrandsPageComponent),
          },
          {
            path: `:${RouteParam.Slug}`,
            loadComponent: () =>
              import('./pages/brand').then((c) => c.BrandDetailsPageComponent),
          },
        ],
      },
      {
        path: RouteFragment.PL_OrderBox,
        title: resolveTitleFor(Route.PL_OrderBox),
        loadComponent: () =>
          import('./pages/order').then((c) => c.OrderBoxPageComponent),
      },
      {
        path: RouteFragment.PL_Boxes,
        children: [
          {
            path: RouteFragment.Empty,
            title: resolveTitleFor(Route.PL_Boxes),
            loadComponent: () =>
              import('./pages/box').then((c) => c.BoxesPageComponent),
          },
          {
            path: `:${RouteParam.Slug}/${RouteFragment.PL_Authorization}`,
            title: resolveTitleFor(Route.PL_BoxDetailsAuthorization),
            canActivate: [PublicBoxAuthorizationGuard],
            loadComponent: () =>
              import('./pages/box').then(
                (c) => c.BoxDetailsAuthorizationPageComponent,
              ),
          },
          {
            path: `:${RouteParam.Slug}`,
            title: resolveTitleFor(Route.PL_BoxDetails),
            loadComponent: () =>
              import('./pages/box').then((c) => c.BoxDetailsPageComponent),
            canActivate: [PublicBoxAuthorizedDetailsGuard],
          },
        ],
      },
      {
        path: RouteFragment.PL_Cosmetics,
        children: [
          {
            path: `:${RouteParam.Slug}`,
            title: resolveTitleFor(Route.PL_CosmeticDetails),
            loadComponent: () =>
              import('./pages/cosmetic').then(
                (c) => c.CosmeticDetailsPageComponent,
              ),
          },
        ],
      },
      {
        path: RouteFragment.PL_Products,
        children: [
          {
            path: `:${RouteParam.Slug}/${RouteFragment.PL_Authorization}`,
            title: resolveTitleFor(Route.PL_ProductDetailsAuthorization),
            canActivate: [PublicProductAuthorizationGuard],
            loadComponent: () =>
              import('./pages/product').then(
                (c) => c.ProductDetailsAuthorizationPageComponent,
              ),
          },
          {
            path: `:${RouteParam.Slug}`,
            title: resolveTitleFor(Route.PL_ProductDetails),
            loadComponent: () =>
              import('./pages/product').then(
                (c) => c.ProductDetailsPageComponent,
              ),
            canActivate: [PublicProductAuthorizedDetailsGuard],
          },
          {
            path: RouteFragment.Empty,
            title: resolveTitleFor(Route.PL_Products),
            loadComponent: () =>
              import('./pages/product').then((c) => c.ProductsPageComponent),
          },
        ],
      },
      {
        path: RouteFragment.PL_Regulations,
        children: [
          {
            path: RouteFragment.Empty,
            pathMatch: 'full',
            redirectTo: RouteFragment.PL_Shop,
          },
          {
            path: RouteFragment.PL_Shop,
            title: resolveTitleFor(Route.PL_RegulationsOfShop),
            loadComponent: () =>
              import('./pages/regulation').then(
                (c) => c.RegulationOfShopPageComponent,
              ),
          },
          {
            path: RouteFragment.PL_PublishingOpinions,
            title: resolveTitleFor(Route.PL_RegulationsOfPublishingOpinions),
            loadComponent: () =>
              import('./pages/regulation').then(
                (c) => c.RegulationOfPublishingOpinionsPageComponent,
              ),
          },
        ],
      },
      {
        path: RouteFragment.PL_PrivacyPolicy,
        title: resolveTitleFor(Route.PL_PrivacyPolicy),
        loadComponent: () =>
          import('./pages/privacy-policy').then(
            (c) => c.PrivacyPolicyPageComponent,
          ),
      },
      {
        path: `${RouteFragment.PL_Page}/:${RouteParam.Slug}`,
        loadComponent: () =>
          import('./pages/page').then((c) => c.PageBySlugPageComponent),
      },
      {
        path: RouteFragment.PL_FAQ,
        title: resolveTitleFor(Route.PL_FAQ),
        resolve: {
          [RouteResolverData.Categories]: publicFetchFaqCategoriesResolver,
        },
        loadComponent: () =>
          import('./pages/faq').then(
            (c) => c.FrequentlyAskedQuestionsPageComponent,
          ),
      },
      {
        path: RouteFragment.PL_Contact,
        title: resolveTitleFor(Route.PL_Contact),
        loadComponent: () =>
          import('./pages/contact').then((c) => c.ContactPageComponent),
      },
      {
        path: RouteFragment.PL_Checkout,
        title: resolveTitleFor(Route.PL_Checkout),
        loadComponent: () =>
          import('./pages/order').then((c) => c.CheckoutPageComponent),
      },
      {
        path: RouteFragment.PL_Payment,
        title: resolveTitleFor(Route.PL_Payment),
        loadComponent: () =>
          import('./pages/order').then((c) => c.CheckoutPaymentPageComponent),
      },
      {
        path: RouteFragment.PL_ShoppingCart,
        title: resolveTitleFor(Route.PL_ShoppingCartDetails),
        loadComponent: () =>
          import('./pages/order').then((c) => c.CartDetailsPageComponent),
      },
      {
        path: `${RouteFragment.PL_Order}/:${RouteParam.Guid}/${RouteFragment.PL_Summary}/:${RouteParam.Token}`,
        title: resolveTitleFor(Route.PL_OrderSummaryWithToken),
        loadComponent: () =>
          import('./pages/order').then((c) => c.OrderSummaryPageComponent),
      },
      {
        path: `${RouteFragment.PL_Order}/:${RouteParam.Guid}/${RouteFragment.PL_Redirection}/:${RouteParam.Token}`,
        title: resolveTitleFor(Route.PL_OrderPaymentRedirection),
        loadComponent: () =>
          import('./pages/order').then(
            (c) => c.OrderPaymentRedirectionPageComponent,
          ),
      },
    ],
  },
  {
    path: RouteFragment.Empty,
    loadComponent: () =>
      import('./layouts/layout').then((c) => c.StandardLayoutComponent),
    canActivate: [OnlyLoggedGuard],
    children: [
      {
        path: RouteFragment.PL_Account,
        title: resolveTitleFor(Route.PL_Account),
        loadComponent: () =>
          import('./pages/account').then((c) => c.AccountPageComponent),
        children: [
          {
            path: RouteFragment.Empty,
            pathMatch: 'full',
            redirectTo: RouteFragment.PL_Details,
          },
          {
            path: `${RouteFragment.PL_Orders}/:${RouteParam.Number}`,
            title: resolveTitleFor(Route.PL_AccountOrderDetails),
            loadComponent: () =>
              import('./pages/account').then(
                (c) => c.AccountOrderDetailsPageComponent,
              ),
          },
          {
            path: RouteFragment.PL_Orders,
            title: resolveTitleFor(Route.PL_AccountOrders),
            loadComponent: () =>
              import('./pages/account').then(
                (c) => c.AccountOrdersPageComponent,
              ),
          },
          {
            path: RouteFragment.PL_Questionnaires,
            children: [
              {
                path: RouteFragment.Empty,
                title: resolveTitleFor(Route.PL_AccountQuestionnaires),
                loadComponent: () =>
                  import('./pages/account').then(
                    (c) => c.AccountQuestionnairesPageComponent,
                  ),
              },
              {
                path: `:${RouteParam.Guid}`,
                title: resolveTitleFor(Route.PL_AccountQuestionnaireDetails),
                canDeactivate: [accountQuestionnaireDetailsPendingChangesGuard],
                loadComponent: () =>
                  import('./pages/account').then(
                    (c) => c.AccountQuestionnaireDetailsPageComponent,
                  ),
              },
            ],
          },
          {
            path: `${RouteFragment.PL_Subscriptions}/:${RouteParam.Number}`,
            title: resolveTitleFor(Route.PL_AccountSubscriptionDetails),
            loadComponent: () =>
              import('./pages/account').then(
                (c) => c.AccountSubscriptionDetailsPageComponent,
              ),
          },
          {
            path: RouteFragment.PL_Subscriptions,
            title: resolveTitleFor(Route.PL_AccountSubscriptions),
            loadComponent: () =>
              import('./pages/account').then(
                (c) => c.AccountSubscriptionsPageComponent,
              ),
          },
          {
            path: RouteFragment.PL_BeautyProfile,
            title: resolveTitleFor(Route.PL_AccountBeautyProfile),
            loadComponent: () =>
              import('./pages/account').then(
                (c) => c.AccountBeautyProfilePageComponent,
              ),
          },
          {
            path: RouteFragment.PL_Details,
            title: resolveTitleFor(Route.PL_AccountDetails),
            loadComponent: () =>
              import('./pages/account').then(
                (c) => c.AccountDetailsPageComponent,
              ),
          },
          {
            path: RouteFragment.PL_Addresses,
            title: resolveTitleFor(Route.PL_AccountAddresses),
            loadComponent: () =>
              import('./pages/account').then(
                (c) => c.AccountAddressesPageComponent,
              ),
          },
        ],
      },
    ],
  },
  {
    path: RouteFragment.SignIn,
    loadComponent: () =>
      import('./pages/auth').then((c) => c.SignInPageComponent),
  },
  {
    path: RouteFragment.SignOut,
    loadComponent: () =>
      import('./pages/auth').then((c) => c.SignOutPageComponent),
  },
  {
    path: RouteFragment.SignUp,
    loadComponent: () =>
      import('./pages/auth').then((c) => c.SignUpPageComponent),
  },
  {
    path: RouteFragment.Redirect,
    loadComponent: () =>
      import('./pages/redirection').then((c) => c.RedirectionPageComponent),
  },
  {
    path: RouteFragment.Loading,
    title: resolveTitleFor(Route.Loading),
    loadComponent: () =>
      import('./pages/loading').then((c) => c.LoadingPageComponent),
  },
  {
    path: RouteFragment.Wildcard,
    redirectTo: RouteFragment.NotFound,
  },
];
